import { Typography, Box, Stack, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IBreadcrumbsAndMenu,
  IExporter,
  IMenuOption,
  ISelectOption,
} from "../../../types/global";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import CustomizedBreadcrumbs from "../../../components/Custom/CustomizedBreadcrumbs";
import UserTable from "../../../components/Table/User";
import { useModal } from "../../../hooks/use-modal";
import { createGraphQLClientWithMiddleware } from "../../../services/graphqlClient";
import { useEffect, useState } from "react";
import {
  ExportGenerateInput,
  GeneralExportType,
  GenerateUserReportQuery,
  useGenerateUserReportQuery,
} from "../../../generated/general";
import { useSnackbar } from "notistack";
import ExporterModal from "../../../components/UI/Modal/ExporterModal";
import CustomizedMenuOptions from "../../../components/Custom/CustomizedMenuOptions";

const userTypeOptions: ISelectOption[] = [
  {
    label: "บัญชีผู้ใช้",
    value: "user",
  },
];

const userOptions: IMenuOption[] = [
  {
    value: "นำเข้าข้อมูล",
  },
  {
    value: "นำออกข้อมูล",
  },
];

const UserAccount = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const breadcrumbs: IBreadcrumbsAndMenu[] = [
    {
      name: t("user.index"),
      to: "/user",
    },
    {
      name: t("user.account.index"),
    },
  ];

  const { modal, openModalHandler, closeModalHandler } = useModal();

  const graphqlClient = createGraphQLClientWithMiddleware("general");

  const [exportInput, setExportInput] = useState<ExportGenerateInput | null>(
    null
  );

  const { refetch: refetchData, isLoading: isExporting } =
    useGenerateUserReportQuery<GenerateUserReportQuery>(
      graphqlClient,
      {
        input: exportInput,
      },
      {
        enabled: false,
      }
    );

  const exportHandler = async (data: IExporter) => {
    if (data) {
      const { type, start_date, end_date } = data;
      setExportInput({
        report_type: type as GeneralExportType,
        start_date: start_date,
        end_date: end_date,
      });
    }
  };

  useEffect(() => {
    const fetchDataAndOpenWindow = async () => {
      try {
        const { data: exportData } = await refetchData();
        window.open(exportData?.GenerateReportNoQueue || "", "_blank");
        enqueueSnackbar(`${t("button.export")}สำเร็จ`, {
          variant: "success",
        });
      } catch (error) {
        console.error("Error fetching data:", error);
        enqueueSnackbar(`${t("button.export")}ไม่สำเร็จ`, {
          variant: "error",
        });
      }
    };

    if (exportInput !== null) {
      fetchDataAndOpenWindow();
    }
  }, [enqueueSnackbar, exportInput, refetchData, t]);

  if (exportInput && isExporting) {
    return (
      <Box
        sx={{
          height: "calc(100dvh - 176px)",
          marginRight: "260px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
      <Box sx={{ display: "flex", justifyContent: "space-between", my: 3 }}>
        <Typography variant="h5">{t("user.account.index")}</Typography>
        <Stack direction={"row"} gap={1}>
          <CustomizedButton
            title={t("user.account.add_new_account")}
            variant="contained"
            onClick={() => navigate(`${pathname}/add?tab=branch`)}
            addIcon
          />
          <CustomizedMenuOptions
            isIcon
            options={userOptions}
            onSelect={(e) => {
              const value = e.target as HTMLElement;
              switch (value.innerText) {
                case `${t("button.import")}`:
                  navigate(`${pathname}/import`);
                  break;
                case `${t("button.export")}`:
                  openModalHandler();
                  break;
                default:
              }
            }}
          />
        </Stack>
      </Box>
      <UserTable />
      <ExporterModal
        open={modal}
        closeModalHandler={closeModalHandler}
        typeOptions={userTypeOptions}
        exportHandler={exportHandler}
      />
    </>
  );
};

export default UserAccount;
